import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { computeRotationAngle } from '@/lib/boundaries/bounding-box-boundary'
import { snapVisualResizeBoundsToGrid } from '@/lib/guides/grid-snap'
import { isGroupShape } from '@/lib/groups/group-kind'
import { buildUnifiedSelectionShapeResizeCommitItems } from '@/lib/transforms/unified-selection-resize'
import {
  MOVE_CLICK_THRESHOLD_PX,
  SHAPE_MIN_DIMENSION,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import {
  buildGroupTransformCommitItems,
  buildSingleShapeCommitUndo,
} from '../gestures/gesture-commit'
import { commitMultiMove, computeMoveSmartGuideSnap } from '../gestures/move-gesture'
import { clampResizeToPasteboardMax } from '../gestures/pasteboard-clamp'
import { rescaleCommittedPath } from '../gestures/path-rescale'
import { applyPhotoFillResizeClamp } from '../gestures/photo-fill-resize'
import { applyMultiSelectResizeStep, computeSingleObjectResize } from '../gestures/resize-gesture'
import { commitMultiRotate, computeSingleRotateCommit } from '../gestures/rotate-commit'
import type { MultiRotateInteraction } from '../gestures/rotate-gesture'
import { isPastMoveClickThreshold } from '../helpers/is-past-move-click-threshold'
import { releasePointerCaptureIfHeld } from '../helpers/pointer-capture'
import type { InteractionState } from '../types/interaction-state'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SelectionHistoryApi } from './use-selection-history'
import type { SnapRefsApi } from './use-snap-refs'
import type { ShapeUpdatesApi } from './use-shape-updates'
import type { SmartGuidesApi } from './use-smart-guides'
import type { InteractionControlApi } from './use-interaction-control'
import type { GestureSnappingApi } from './use-gesture-snapping'

export function useGestureCommit(
  ctx: ShapeManagerStateApi &
    SelectionHistoryApi &
    SnapRefsApi &
    ShapeUpdatesApi &
    SmartGuidesApi &
    InteractionControlApi &
    GestureSnappingApi
) {
  const {
    resizeMaxDimensionToastActiveRef,
    unitOfMeasure,
    effectiveSnapGuideDistance,
    emitSmartGuides,
    finishInteraction,
    getGuideSnappedMoveDelta,
    getMovedSelectionBounds,
    getOtherShapeBounds,
    gridSnapLiveRectsRef,
    gridSnapStepRef,
    interactionRef,
    layoutSnapLines,
    multiSelectChromeRef,
    multiSelectSyncPrevShapesRef,
    onBatchShapesCommitted,
    onShapeCommitted,
    panelDimensionsLinkedRef,
    pendingRotateChromeBeforeRef,
    promoteAltDragDuplicateRef,
    recordShapeSelectionHistory,
    savedBodyCursorForMultiMoveRef,
    scaleRef,
    selectedShapeIdsRef,
    setMultiSelectChrome,
    setSelectedShapeIds,
    shapesRef,
    smartGuidesEnabledRef,
    snapToGridRef,
    snapToGuidesRef,
    updateShapeRef,
  } = ctx

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      const interactionAtRelease = interactionRef.current

      if (!interactionAtRelease || event.pointerId !== interactionAtRelease.pointerId) {
        return
      }

      const isRelease = event.type === 'pointerup'
      const releaseScale = scaleRef.current || 1

      // A pointer that passed the click threshold with no move event in between is still a drag.
      if (
        isRelease &&
        interactionAtRelease.type === 'move' &&
        !interactionAtRelease.hasDragged &&
        isPastMoveClickThreshold(
          (event.clientX - interactionAtRelease.startClientX) / releaseScale,
          (event.clientY - interactionAtRelease.startClientY) / releaseScale
        )
      ) {
        interactionRef.current = { ...interactionAtRelease, hasDragged: true }
        promoteAltDragDuplicateRef.current()
      }

      const interaction = interactionRef.current ?? interactionAtRelease

      const isMultiMoveCursor = interaction.type === 'move' && interaction.multiMoveInitials
      const isResizeCursorLock = interaction.type === 'resize' && interaction.direction
      const getShapes = () => shapesRef.current

      try {
        const scaleFactor = scaleRef.current || 1
        const dx = (event.clientX - interaction.startClientX) / scaleFactor
        const dy = (event.clientY - interaction.startClientY) / scaleFactor
        const latest = {
          dx,
          dy,
          clientX: event.clientX,
          clientY: event.clientY,
          shiftKey: event.shiftKey,
        }
        let snappedMoveDelta = getGuideSnappedMoveDelta(latest.dx, latest.dy, interaction)

        if (smartGuidesEnabledRef.current && interaction.type === 'move') {
          const snap = computeMoveSmartGuideSnap(interaction, snappedMoveDelta, {
            shapes: shapesRef.current,
            getOtherShapeBounds,
            getMovedSelectionBounds,
            effectiveSnapGuideDistance,
            layoutSnapLines: snapToGuidesRef.current ? layoutSnapLines : undefined,
          })

          if (snap) {
            snappedMoveDelta = snap.delta
          }
        }

        // Click without drag: grid/guide snap still shifts x/y when raw delta is 0 (BUG 1625).
        if (
          interaction.type === 'move' &&
          Math.abs(dx) < MOVE_CLICK_THRESHOLD_PX &&
          Math.abs(dy) < MOVE_CLICK_THRESHOLD_PX
        ) {
          snappedMoveDelta = { dx: 0, dy: 0 }
        }

        emitSmartGuides([])

        // --- Multi-select move path ---
        if (interaction.type === 'move' && interaction.multiMoveInitials) {
          const isClick =
            Math.abs(dx) < MOVE_CLICK_THRESHOLD_PX && Math.abs(dy) < MOVE_CLICK_THRESHOLD_PX

          // If it was a click (no significant movement) and a shape was clicked, select just that shape
          if (isClick && interaction.clickedShapeInMultiSelect) {
            setSelectedShapeIds((prev) => {
              const next = [interaction.clickedShapeInMultiSelect!]

              selectedShapeIdsRef.current = next
              recordShapeSelectionHistory(prev, next)
              return next
            })

            // Clean up
            releasePointerCaptureIfHeld(event.target, event.pointerId)

            event.stopPropagation()
            finishInteraction()
            return
          }

          commitMultiMove(
            interaction as InteractionState & Required<Pick<InteractionState, 'multiMoveInitials'>>,
            snappedMoveDelta,
            {
              updateShape: updateShapeRef.current,
              setMultiSelectChrome,
              multiSelectSyncPrevShapesRef,
              getShapes,
              onBatchShapesCommitted,
            }
          )

          // Clean up
          releasePointerCaptureIfHeld(event.target, event.pointerId)

          event.stopPropagation()
          finishInteraction()
          return
        }

        // --- Multi rotate path ---
        if (
          interaction.type === 'rotate' &&
          interaction.multiRotateInitials &&
          interaction.rotatePivotLocal
        ) {
          releasePointerCaptureIfHeld(event.target, event.pointerId)

          event.stopPropagation()
          emitSmartGuides([])

          commitMultiRotate(interaction as MultiRotateInteraction, event, {
            getShapes,
            smartGuidesEnabled: smartGuidesEnabledRef.current,
            updateShape: updateShapeRef.current,
            setMultiSelectChrome,
            pendingRotateChromeBeforeRef,
            onBatchShapesCommitted,
          })

          finishInteraction()
          return
        }

        // --- Multi-select resize ---
        if (interaction.type === 'resize' && interaction.unifiedSelectionResizeSession) {
          const resizeSession = interaction.unifiedSelectionResizeSession

          applyMultiSelectResizeStep(resizeSession, dx, dy, latest.shiftKey, {
            panelDimensionsLinked: panelDimensionsLinkedRef.current,
            updateShape: updateShapeRef.current,
            setMultiSelectChrome,
            multiSelectChromeRef,
          })

          const allShapes = shapesRef.current
          const resizeBatchItems = buildUnifiedSelectionShapeResizeCommitItems({
            initials: resizeSession.shapeInitials,
            shapes: allShapes,
            positionEpsilon: 1e-3,
            sizeEpsilon: 1e-3,
            rotationEpsilon: 1e-2,
            includeRotation: true,
          })

          if (resizeBatchItems.length > 0) {
            pendingRotateChromeBeforeRef.current = { ...resizeSession.chromeStart }
            onBatchShapesCommitted?.(resizeBatchItems, 'resize')
          }

          releasePointerCaptureIfHeld(event.target, event.pointerId)

          event.stopPropagation()
          emitSmartGuides([])

          finishInteraction()
          return
        }

        // --- Single shape path (existing logic) ---
        const finalShape = shapesRef.current.find((s) => s.id === interaction.shapeId)

        releasePointerCaptureIfHeld(event.target, event.pointerId)

        event.stopPropagation()

        const baseShape =
          shapesRef.current.find((s) => s.id === interaction.shapeId) ?? interaction.initial
        let committedShape: ShapeData = baseShape

        let resyncPlan: null | {
          kind: 'rotate'
          params: Parameters<typeof computeRotationAngle>[0]
          opts?: Parameters<typeof computeRotationAngle>[1]
        } = null

        if (interaction.type === 'move') {
          if (interaction.multiMoveInitials) {
            Object.entries(interaction.multiMoveInitials).forEach(([id, pos]) => {
              updateShapeRef.current(id, {
                x: pos.x + snappedMoveDelta.dx,
                y: pos.y + snappedMoveDelta.dy,
              })
            })
            committedShape = {
              ...committedShape,
              x: interaction.initial.x + snappedMoveDelta.dx,
              y: interaction.initial.y + snappedMoveDelta.dy,
            }
          } else {
            const updates = {
              x: interaction.initial.x + snappedMoveDelta.dx,
              y: interaction.initial.y + snappedMoveDelta.dy,
            }

            updateShapeRef.current(interaction.shapeId, updates)
            committedShape = { ...committedShape, ...updates }
          }
        } else if (interaction.type === 'resize' && interaction.direction) {
          const resized = computeSingleObjectResize(interaction, interaction.direction, latest, {
            panelDimensionsLinked: panelDimensionsLinkedRef.current,
            smartGuidesEnabled: smartGuidesEnabledRef.current,
            getOtherShapeBounds,
            effectiveSnapGuideDistance,
            layoutSnapLines: snapToGuidesRef.current ? layoutSnapLines : undefined,
          })
          const { dir, useAspectRatio, visualRatios } = resized
          let { next } = resized

          if (snapToGridRef.current) {
            const step = Math.max(1, Math.round(Number(gridSnapStepRef.current)) || 1)

            // Snap the edge the user can SEE onto the gridline, not the generator box's edge —
            // for an inset shape those are different lines. Identity ratios (rectangles) behave
            // exactly as before.
            next = snapVisualResizeBoundsToGrid(
              next,
              visualRatios,
              dir,
              step,
              gridSnapLiveRectsRef.current,
              SHAPE_MIN_DIMENSION
            )
          }

          const { next: clampedNext, photoFillUpdate } = applyPhotoFillResizeClamp(
            interaction.initial,
            dir,
            next,
            useAspectRatio
          )

          next = clampedNext

          next = clampResizeToPasteboardMax(
            {
              initial: interaction.initial,
              direction: dir,
              next,
              aspectLocked: useAspectRatio,
            },
            resizeMaxDimensionToastActiveRef,
            unitOfMeasure
          )

          updateShapeRef.current(
            interaction.shapeId,
            photoFillUpdate ? { ...next, ...photoFillUpdate } : next
          )
          committedShape = { ...committedShape, ...next, ...photoFillUpdate }
          committedShape = rescaleCommittedPath(baseShape, next, committedShape)
        } else if (interaction.type === 'rotate' && interaction.overlayRect) {
          const { rotationDeg, params, snapped } = computeSingleRotateCommit(
            interaction as InteractionState & { overlayRect: DOMRect },
            latest,
            { getShapes, smartGuidesEnabled: smartGuidesEnabledRef.current }
          )

          updateShapeRef.current(interaction.shapeId, { rotation: rotationDeg })
          committedShape = { ...committedShape, rotation: rotationDeg }

          // Async WASM resync uses the raw pointer; it would overwrite a snapped angle.
          if (!snapped) {
            resyncPlan = {
              kind: 'rotate',
              params,
              opts: { mode: 'adaptive', hotMs: 0, noTelemetry: true },
            }
          }
        }

        finishInteraction()

        if (resyncPlan) {
          void (async () => {
            try {
              if (resyncPlan.kind === 'rotate') {
                const resynced = await computeRotationAngle(resyncPlan.params, resyncPlan.opts)
                const EPS_DEG = 1e-2

                if (Math.abs(resynced.degrees - (committedShape.rotation ?? 0)) > EPS_DEG) {
                  updateShapeRef.current(interaction.shapeId, { rotation: resynced.degrees })
                }
              }
            } catch {
              // Ignore re-sync errors; drag end already committed via JS.
            }
          })()
        }

        const commitUndo = buildSingleShapeCommitUndo(interaction, committedShape)

        if (
          interaction.groupTransformInitials &&
          isGroupShape(interaction.initial) &&
          (interaction.type === 'move' ||
            interaction.type === 'resize' ||
            interaction.type === 'rotate')
        ) {
          const batchSource = interaction.type
          const items = buildGroupTransformCommitItems(
            interaction.groupTransformInitials,
            batchSource,
            shapesRef.current
          )

          const mixedHistoryTxnId = `mixed-${batchSource}:${Date.now()}:${Math.random()
            .toString(36)
            .slice(2, 9)}`

          if (items.length > 0) {
            onBatchShapesCommitted?.(items, batchSource, { historyTxnId: mixedHistoryTxnId })
          }

          if (batchSource === 'move') {
            pendingRotateChromeBeforeRef.current = null
          }

          return
        }

        onShapeCommitted?.(
          committedShape ?? finalShape ?? interaction.initial,
          interaction.type,
          commitUndo
        )
      } finally {
        if (
          (isMultiMoveCursor || isResizeCursorLock) &&
          savedBodyCursorForMultiMoveRef.current !== null
        ) {
          document.body.style.cursor = savedBodyCursorForMultiMoveRef.current
          savedBodyCursorForMultiMoveRef.current = null
        }

        if (isRelease && interaction.type === 'move' && !interaction.hasDragged) {
          interaction.onClickRelease?.()
        }
      }
    },
    [
      resizeMaxDimensionToastActiveRef,
      unitOfMeasure,
      effectiveSnapGuideDistance,
      emitSmartGuides,
      finishInteraction,
      getGuideSnappedMoveDelta,
      getMovedSelectionBounds,
      getOtherShapeBounds,
      gridSnapLiveRectsRef,
      gridSnapStepRef,
      interactionRef,
      layoutSnapLines,
      multiSelectChromeRef,
      multiSelectSyncPrevShapesRef,
      onBatchShapesCommitted,
      onShapeCommitted,
      panelDimensionsLinkedRef,
      pendingRotateChromeBeforeRef,
      promoteAltDragDuplicateRef,
      recordShapeSelectionHistory,
      savedBodyCursorForMultiMoveRef,
      scaleRef,
      selectedShapeIdsRef,
      setMultiSelectChrome,
      setSelectedShapeIds,
      shapesRef,
      smartGuidesEnabledRef,
      snapToGridRef,
      snapToGuidesRef,
      updateShapeRef,
    ]
  )

  return { handlePointerUp }
}

export type GestureCommitApi = ReturnType<typeof useGestureCommit>
